import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { attendanceService } from '../../common/service/AttendanceService'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import { Button, Card, Form, Input, Typography, DatePicker, Modal, Space, Row } from 'antd'
import { Colors, useWidth } from '../../common/constant'
import { useThemeColors } from '../../common/ThemeContext'
import type { DatePickerProps, RangePickerProps } from 'antd/es/date-picker'
import _, { forEach } from 'lodash'
import SuccessfulModal from '../../common/components/ConfirmModel'

interface AttendanceEditorPageProps {
	attendanceIdOverride?: string
	embedded?: boolean
	onClose?: () => void
	onSuccess?: () => void
}

function AttendanceEditorPage({
	attendanceIdOverride,
	embedded = false,
	onClose,
	onSuccess,
}: AttendanceEditorPageProps) {
	const { primary } = useThemeColors()
	const { id: routeAttendanceId } = useParams()
	const attendanceID = attendanceIdOverride || routeAttendanceId
	const layout = {
		labelCol: { span: 8 },
		wrapperCol: { span: 16 },
	}
	const INITIAL_STATE: any = {
		checkoutReason: '',
		checkOutAt: '',
		id: attendanceID,
	}
	const [formState, setFormState] = React.useState<any>(INITIAL_STATE)
	const [openSuccess, setOpenSuccess] = React.useState<boolean>(false)
	const navigate = useNavigate()

	const onCancel = () => {
		if (embedded) {
			onClose?.()
			return
		}

		navigate('/attendance')
	}

	const toIsoString = (value: DatePickerProps['value']) => {
		if (!value || Array.isArray(value)) {
			return undefined
		}

		return value.toISOString()
	}

	const onOKCheckoutAt = (value: DatePickerProps['value']) => {
		setFormState((prev: any) => ({
			...prev,
			['checkOutAt']: toIsoString(value),
		}))
	}

	const onChangeCheckoutAt = (value: DatePickerProps['value'], dateString: any) => {
		setFormState((prev: any) => ({
			...prev,
			['checkOutAt']: toIsoString(value),
		}))
	}
	const onFinish = async (values: any) => {
		try {
			const { id, checkOutAt, checkoutReason } = formState
			const payload: any = _.omitBy(
				{
					id: id,
					checkOutAt: checkOutAt,
					checkoutReason: checkoutReason,
				},
				_.isNil,
			)
			await attendanceService
				.update(payload)
				.then((data) => {
					if (data?.statusCode === 200) {
						setOpenSuccess(true)
					}
				})
				.catch((err: any) =>
					Modal.error({
						title: 'Attendance Update Failed !',
						content: `Server Response: ${err.response.data.message}`,
					}),
				)
		} catch (err: any) {
			Modal.error({
				title: 'Attendance Update Failed !',
				content: `Unexpected Error : ${err}`,
			})
		}
	}
	return (
		<>
			{!embedded && <GlobalHeader />}
			{!embedded && (
				<div style={{ width: '100%' }}>
					<Card
						color='primary'
						style={{ margin: '24px 16px' }}
						styles={{
							body: {
								fontSize: 18,
								backgroundColor: Colors.White,
								textAlign: 'center',
								borderRadius: 6,
								color: primary,
							},
						}}
					>
						<Typography.Title
							color='secondary'
							aria-level={2}
							style={{
								color: primary,
								fontFamily: 'Times New Roman',
							}}
						>
							Attendance Information
						</Typography.Title>
					</Card>
				</div>
			)}
			<div>
				<Card style={{ margin: '0px 16px' }}>
					<Typography.Title level={2} style={{ marginLeft: '24px' }}>
						Edit Attendance Information
					</Typography.Title>
					<Form
						{...layout}
						name='nest-messages'
						onFinish={onFinish}
						style={{ maxWidth: 600 }}
					>
						<Row></Row>
						<Form.Item name={['attendance', 'id']} label='Attendance ID'>
							<label>{attendanceID} </label>
						</Form.Item>
						<Form.Item name={['attendance', 'checkoutReason']} label='Checkout Reason'>
							<Input
								onChange={(e) => {
									if (e != null) {
										setFormState((prev: any) => ({
											...prev,
											['checkoutReason']: e.target.value,
										}))
									}
								}}
							/>
						</Form.Item>
						<Form.Item name={['attendance', 'checkOutAt']} label='Checkout At'>
							<DatePicker
								showTime={{ format: 'HH:mm' }}
								onChange={onChangeCheckoutAt}
								onOk={onOKCheckoutAt}
							/>
						</Form.Item>
						<Form.Item wrapperCol={{ ...layout.wrapperCol, offset: 8 }}>
							<Space>
								<Button htmlType='button' onClick={onCancel}>
									Cancel
								</Button>
								<Button type='primary' htmlType='submit'>
									Update
								</Button>
							</Space>
						</Form.Item>
					</Form>
				</Card>
				<SuccessfulModal
					title={'Updated successfully'}
					desc={'Attendance is updated successfully.'}
					open={openSuccess}
					onOk={() => {
						if (embedded) {
							onSuccess?.()
							onClose?.()
							setOpenSuccess(false)
							return
						}

						navigate('/attendance')
						setOpenSuccess(false)
					}}
				/>
			</div>
		</>
	)
}

export default AttendanceEditorPage
